import {
  EnvironmentOutlined,
  CalendarOutlined,
  EyeOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  PoundOutlined,
  ProjectOutlined
} from "@ant-design/icons";
import { PageHeader } from "@mj/shared-ui";
import {
  Button,
  Input,
  Pagination,
  Select,
  Tooltip,
  Row,
  Col,
  Skeleton
} from "antd";
import classNames from "classnames";
import React from "react";
import { CiSearch } from "react-icons/ci";
import { useNavigate } from "react-router-dom";

import type { ICentralProjectsSummaryRes } from "@/types/forecasting";

import EmptyState from "@/components/common/EmptyState";
import Layout from "@/components/common/Layout";
import ProjectWorkTypeTag from "@/components/common/ProjectWorkTypeTag";
import {
  UK_REGIONS,
  UK_REGIONS_TO_CODE_MAP,
  CODE_TO_UK_REGIONS_MAP
} from "@/constants/region";
import { canViewAllRegions } from "@/constants/users";
import { useUser } from "@/contexts/UserContext";
import { useProjects } from "@/hooks/businessCentral";
import { useGetCentralProjectsSummary } from "@/hooks/forecasting";
import { useGetAllProjectJobTypes } from "@/hooks/projectJobTypes";
import {
  getProjectWorkType,
  type ProjectWorkType
} from "@/types/projectJobTypes";

const formatCurrency = (value: number) => {
  return `£${value.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/** The per-project extras the cards join onto a BC project by OpportunityNo. */
interface ProjectMeta {
  summary?: ICentralProjectsSummaryRes["Projects"][number];
  workType?: ProjectWorkType;
}

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50];

const DashboardPage = () => {
  const user = useUser();

  // Board Level and System Administrator browse every region; everyone else
  // is pinned to the region on their project-team record.
  const canPickRegion = canViewAllRegions(user?.roleDisplayName);

  const [searchText, setSearchText] = React.useState("");
  const [selectedRegion, setSelectedRegion] = React.useState(() =>
    canPickRegion ? "all" : user?.region
  );
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(DEFAULT_PAGE_SIZE);

  const navigate = useNavigate();

  // Region pickers: "All Regions" sends no region param (null), a picked
  // region sends its code. Everyone else keeps the hook's default — their own
  // region — so they can never fetch other regions' projects.
  const selectedRegionCode = !canPickRegion
    ? undefined
    : selectedRegion !== "all"
      ? UK_REGIONS_TO_CODE_MAP[selectedRegion ?? ""]
      : null;

  const { isLoading: projectsLoading, data: projectsData } =
    useProjects(selectedRegionCode);
  const { isLoading: projectsSummaryLoading, data: projectsSummaryData } =
    useGetCentralProjectsSummary();
  const { isLoading: jobTypesLoading, data: jobTypesData } =
    useGetAllProjectJobTypes();

  // Everything a project card needs beyond the BC record itself, joined into
  // one lookup keyed by OpportunityNo — the only key the three APIs share.
  // Entries on either side without an OpportunityNo can't be matched to a BC
  // project, so they're skipped.
  const projectMetaByOpportunityNo = React.useMemo(() => {
    const map: Record<string, ProjectMeta> = {};

    // Sorted by CreatedAt descending, then written in order, so a repeated
    // OpportunityNo resolves to the same entry on every render. (Unchanged
    // from before the job-types join — last write wins, i.e. the oldest row.)
    [...(projectsSummaryData?.Projects ?? [])]
      .sort(
        (a, b) =>
          new Date(b.CreatedAt).getTime() - new Date(a.CreatedAt).getTime()
      )
      .forEach((p) => {
        if (p.OpportunityNo) {
          map[p.OpportunityNo] = { ...map[p.OpportunityNo], summary: p };
        }
      });

    jobTypesData?.forEach((entry) => {
      if (entry.OpportunityNo) {
        map[entry.OpportunityNo] = {
          ...map[entry.OpportunityNo],
          workType: getProjectWorkType(entry.JobType)
        };
      }
    });

    return map;
  }, [projectsSummaryData, jobTypesData]);

  // Primary source: all BC projects, filtered by region, sorted by creationDate (newest first)
  const projects = React.useMemo(() => {
    if (!projectsData?.value?.length) return [];

    return projectsData.value
      .filter((project) => {
        const regionName = CODE_TO_UK_REGIONS_MAP[project.jobPostingGroup];
        if (!regionName) return false;

        if (selectedRegion === "all") return true;
        return regionName === selectedRegion;
      })
      .sort(
        (a, b) =>
          new Date(b.creationDate).getTime() -
          new Date(a.creationDate).getTime()
      );
  }, [projectsData, selectedRegion]);

  // Filter projects by search. Opportunity-number presence is enforced
  // inside useProjects, so every consumer is already opportunity-only.
  const filteredProjects = React.useMemo(() => {
    return projects.filter((project) => {
      const matchesSearch =
        searchText === "" ||
        project.no.includes(searchText) ||
        project.description.toLowerCase().includes(searchText.toLowerCase());

      return matchesSearch;
    });
  }, [projects, searchText]);

  // Paginate projects
  const paginatedProjects = React.useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredProjects.slice(startIndex, startIndex + pageSize);
  }, [filteredProjects, currentPage, pageSize]);

  const rangeStart = (currentPage - 1) * pageSize + 1;
  const rangeEnd = Math.min(currentPage * pageSize, filteredProjects.length);

  // Reset to page 1 when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchText, selectedRegion]);

  return (
    <Layout>
      <PageHeader title="Projects Dashboard" />
      <Skeleton
        loading={projectsLoading || projectsSummaryLoading || jobTypesLoading}
      >
        <div className="flex flex-wrap gap-3 mb-6">
          <Select
            size="large"
            value={selectedRegion}
            onChange={(value) => setSelectedRegion(value)}
            disabled={!canPickRegion}
            style={{ width: 200 }}
            placeholder="Select Region"
            showSearch
            prefix={<EnvironmentOutlined />}
            filterOption={(input, option) => {
              const label = option?.label?.toString().toLowerCase() || "";
              const searchInput = input.toLowerCase();
              return label.includes(searchInput);
            }}
            options={[
              { value: "all", label: "All Regions" },
              ...UK_REGIONS.map((region) => ({
                value: region,
                label: region
              }))
            ]}
          />
          <Input
            size="large"
            placeholder="Search projects..."
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            prefix={
              <span className="inline-flex justify-center items-center mr-1">
                <CiSearch size={20} />
              </span>
            }
            style={{ maxWidth: 300 }}
          />
        </div>

        {/* Project Cards */}
        {filteredProjects.length === 0 ? (
          <EmptyState
            icon={<ProjectOutlined />}
            title="No projects found"
            description={
              projects.length === 0
                ? "There are no projects to display for the selected region yet."
                : "No projects match your search. Try a different search term."
            }
          />
        ) : (
          <Row gutter={[16, 16]}>
            {paginatedProjects.map((project) => {
              const meta = project.opportunityNo
                ? projectMetaByOpportunityNo[project.opportunityNo]
                : undefined;
              const summary = meta?.summary;
              const hasForecasted = summary?.HasBeenForecasted;
              const variance =
                hasForecasted && project.budgetTotalCost
                  ? project.budgetTotalCost - summary.TotalForecastedCost
                  : 0;

              return (
                <Col key={project.id} xs={24} sm={24}>
                  <div className="bg-white rounded-lg border border-gray-200 p-5 flex items-center">
                    {/* Left blue accent bar */}
                    <div className="w-1 h-24 bg-blue-500 rounded-full mr-5 self-stretch" />

                    {/* Left section */}
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-lg font-semibold leading-tight text-gray-900 m-0">
                          {project.no} - {project.description}
                        </h3>
                        <ProjectWorkTypeTag
                          workType={meta?.workType ?? "unknown"}
                        />
                      </div>
                      <div className="flex flex-col gap-1 text-gray-600 text-sm">
                        <div className="flex items-center gap-2">
                          <CalendarOutlined className="text-gray-400" />
                          <span>
                            Created:{" "}
                            {new Date(project.creationDate).toLocaleDateString(
                              "en-GB"
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right section */}
                    <div className="flex items-center gap-8">
                      {hasForecasted ? (
                        <>
                          <div className="flex flex-col gap-1 text-sm text-gray-600">
                            <div className="flex items-center gap-2">
                              <PoundOutlined className="text-gray-400" />
                              <span>
                                Total Tender Budget:{" "}
                                <span className="font-medium text-gray-900">
                                  {project.budgetTotalCost
                                    ? formatCurrency(project.budgetTotalCost)
                                    : "-"}
                                </span>
                              </span>
                              <Tooltip title="Excluding OHP, Preliminaries and Discount">
                                <InfoCircleOutlined className="text-gray-400 cursor-pointer" />
                              </Tooltip>
                            </div>
                            <div className="flex items-center gap-2">
                              <PoundOutlined className="text-gray-400" />
                              <span>
                                Total Forecasted Cost to Date:{" "}
                                <span className="font-medium text-gray-900">
                                  {formatCurrency(summary.TotalForecastedCost)}
                                </span>
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <PoundOutlined className="text-gray-400" />
                              <span>
                                Variance:{" "}
                                <span
                                  className={classNames(
                                    "font-medium",
                                    variance >= 0
                                      ? "text-[#52c41a]"
                                      : "text-[#ff4d4f]"
                                  )}
                                >
                                  {formatCurrency(Math.abs(variance))}
                                </span>
                              </span>
                            </div>
                          </div>
                          <Button
                            type="primary"
                            icon={<EyeOutlined />}
                            size="large"
                            onClick={() => {
                              navigate(
                                `/forecasting?project=${encodeURIComponent(project.description)}`
                              );
                            }}
                            className="min-w-[140px] !bg-[#E6F6FE] !text-[#167EF9]"
                          >
                            View
                          </Button>
                        </>
                      ) : (
                        <Button
                          type="primary"
                          icon={<PlusOutlined />}
                          size="large"
                          onClick={() => {
                            navigate(
                              `/forecasting?project=${encodeURIComponent(project.description)}`
                            );
                          }}
                          className="min-w-[140px] !bg-[#E6F6FE] !text-[#167EF9]"
                        >
                          Create Forecast
                        </Button>
                      )}
                    </div>
                  </div>
                </Col>
              );
            })}
          </Row>
        )}

        {/* Pagination — total on the left, page-size picker on the right,
            matching MJ-Tracker's dashboard. */}
        {filteredProjects.length > 0 && (
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <span className="text-sm text-gray-500">
              {rangeStart}-{rangeEnd} of {filteredProjects.length} projects
            </span>
            <Pagination
              current={currentPage}
              total={filteredProjects.length}
              pageSize={pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              showSizeChanger
              onChange={(page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              }}
            />
          </div>
        )}
      </Skeleton>
    </Layout>
  );
};

export default DashboardPage;
